// siteLayouts.js — Phase 114 "Layouts": four proven, high-converting website
// structures a coach can pick INDEPENDENTLY of the color/font Theme.
// ---------------------------------------------------------------------------
// A layout controls the *shape* of the site, a theme controls its *skin*:
//   • heroLayout    — which hero composition renders (split / photo-card /
//                     classic / bento — see siteSections.jsx HeroSection)
//   • homeOrder     — recommended Home section order (used automatically
//                     until the coach customizes, or on demand via
//                     "Apply recommended order" in the editor)
//   • headingAlign  — left | center for section intros
//   • density       — compact | comfortable | spacious section padding
//   • cardStyle     — outlined | elevated | soft
//   • navStyle      — split (brand · links · CTA) | right (links + CTA right)
//
// Back-compat: when `landing_page.site_layout` is empty we derive the layout
// from the active theme's legacy `heroLayout` so existing sites don't move.

import { getSiteTheme } from '@/lib/siteThemes';

export const SITE_LAYOUTS = {
    'classic-split': {
        id: 'classic-split',
        name: 'Classic Converter',
        tagline: 'Copy left, photo right, proof and pricing right below — the layout most high-converting service sites use.',
        bestFor: 'Most swim coaches · lead generation',
        heroLayout: 'split',
        headingAlign: 'left',
        density: 'comfortable',
        cardStyle: 'elevated',
        navStyle: 'split',
        homeOrder: ['hero', 'trust_strip', 'highlights', 'programs_teaser', 'testimonials', 'locations_teaser', 'travel_check', 'faq', 'cta_band'],
        // Wireframe rows for the editor thumbnail: each row is [blocks…]
        // where a block is { w: flex-grow, h: px, kind: 'text'|'img'|'cards'|'band' }.
        sketch: [
            [{ w: 1, h: 26, kind: 'text' }, { w: 1, h: 26, kind: 'img' }],
            [{ w: 1, h: 6, kind: 'band' }],
            [{ w: 1, h: 14, kind: 'cards' }],
            [{ w: 1, h: 14, kind: 'cards' }],
            [{ w: 1, h: 8, kind: 'dark' }],
        ],
    },
    immersive: {
        id: 'immersive',
        name: 'Immersive Photo',
        tagline: 'Full-bleed hero photo with a floating headline card, then gallery and programs — brand-forward like the big swim schools.',
        bestFor: 'Great photography · lifestyle brands',
        heroLayout: 'photo-card',
        headingAlign: 'center',
        density: 'comfortable',
        cardStyle: 'soft',
        navStyle: 'split',
        homeOrder: ['hero', 'trust_strip', 'gallery', 'highlights', 'programs_teaser', 'testimonials', 'locations_teaser', 'travel_check', 'faq', 'cta_band'],
        sketch: [
            [{ w: 1, h: 34, kind: 'img-card' }],
            [{ w: 1, h: 6, kind: 'band' }],
            [{ w: 1, h: 12, kind: 'gallery' }],
            [{ w: 1, h: 14, kind: 'cards' }],
            [{ w: 1, h: 8, kind: 'dark' }],
        ],
    },
    editorial: {
        id: 'editorial',
        name: 'Editorial Minimal',
        tagline: 'Centered headline, one big photo, generous whitespace and a calm story-first flow — premium and trustworthy.',
        bestFor: 'Premium private coaching · therapy & adult swim',
        heroLayout: 'classic',
        headingAlign: 'center',
        density: 'spacious',
        cardStyle: 'outlined',
        navStyle: 'right',
        homeOrder: ['hero', 'story', 'highlights', 'testimonials', 'programs_teaser', 'trust_strip', 'faq', 'cta_band'],
        sketch: [
            [{ w: 1, h: 16, kind: 'text-center' }],
            [{ w: 1, h: 22, kind: 'img' }],
            [{ w: 1, h: 12, kind: 'text-center' }],
            [{ w: 1, h: 14, kind: 'cards' }],
            [{ w: 1, h: 8, kind: 'dark' }],
        ],
    },
    bento: {
        id: 'bento',
        name: 'Bento Modern',
        tagline: 'Headline plus stat tiles and a photo tile, tighter spacing and a grid feel — athletic, modern, data-driven.',
        bestFor: 'Competitive swim · triathlon · technique clinics',
        heroLayout: 'bento',
        headingAlign: 'left',
        density: 'compact',
        cardStyle: 'elevated',
        navStyle: 'split',
        homeOrder: ['hero', 'trust_strip', 'programs_teaser', 'highlights', 'testimonials', 'faq', 'locations_teaser', 'travel_check', 'cta_band'],
        sketch: [
            [{ w: 1.1, h: 30, kind: 'text-tiles' }, { w: 0.9, h: 30, kind: 'img' }],
            [{ w: 1, h: 6, kind: 'band' }],
            [{ w: 1, h: 14, kind: 'cards' }],
            [{ w: 1, h: 12, kind: 'cards' }],
            [{ w: 1, h: 8, kind: 'dark' }],
        ],
    },
};

export const SITE_LAYOUT_LIST = Object.values(SITE_LAYOUTS);

// Legacy theme heroLayout → layout id (for sites saved before Layouts shipped).
const HERO_TO_LAYOUT = {
    split: 'classic-split',
    'photo-card': 'immersive',
    centered: 'editorial',
    classic: 'editorial',
    bento: 'bento',
};

// Section padding (mobile / desktop) per density — consumed as CSS vars.
const DENSITY_VARS = {
    compact: { '--l-py-m': '2.75rem', '--l-py-d': '4rem' },
    comfortable: { '--l-py-m': '3.5rem', '--l-py-d': '6rem' },
    spacious: { '--l-py-m': '4.25rem', '--l-py-d': '7.5rem' },
};

/**
 * Resolve the active layout for a landing-page payload. When no explicit
 * `site_layout` is saved we keep the theme's original hero composition so
 * nothing moves for existing coaches.
 */
export function getSiteLayout(lp) {
    const explicit = lp?.site_layout;
    if (explicit && SITE_LAYOUTS[explicit]) {
        return { ...SITE_LAYOUTS[explicit], vars: DENSITY_VARS[SITE_LAYOUTS[explicit].density] };
    }
    const theme = getSiteTheme(lp);
    const derived = SITE_LAYOUTS[HERO_TO_LAYOUT[theme.heroLayout] || 'classic-split'];
    return { ...derived, heroLayout: theme.heroLayout, derived: true, vars: DENSITY_VARS[derived.density] };
}
